import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import {
  ArrowBack,
  Add,
  Delete,
  Edit,
  PlayArrow,
  Style,
  LibraryBooks,
} from '@mui/icons-material';
import CustomAlert from '../components/CustomAlert';

const SetDetailScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const {
    flashcardSets,
    studiedSets,
    flashcards,
    setCurrentSet,
    deleteFlashcardSet,
    deleteFlashcard,
  } = useFlashcards();

  const setId = route.params?.setId;
  const [deleteSetModal, setDeleteSetModal] = useState(false);
  const [deleteCardModal, setDeleteCardModal] = useState({ visible: false, cardId: null });
  const [alert, setAlert] = useState({ visible: false, title: '', message: '' });

  // Find the set (check both active and studied sets)
  const activeSet = flashcardSets.find(set => set.id === setId);
  const studiedSet = (studiedSets || []).find(set => set.id === setId);
  const setItem = activeSet || studiedSet;

  // Get flashcards for this set
  const setFlashcards = flashcards.filter(card => card.setId === setId);

  // Set current set when component mounts or setId changes
  useEffect(() => {
    if (setItem && setId) {
      setCurrentSet(setItem);
    }
  }, [setId]); // Only depend on setId to avoid infinite loops

  // If set not found, show error and navigate back
  useEffect(() => {
    if (!setItem && flashcardSets.length > 0 && studiedSets.length > 0) {
      setAlert({
        visible: true,
        title: 'Error',
        message: 'Set not found',
      });
      setTimeout(() => {
        navigation.goBack();
      }, 2000);
    }
  }, [setItem, flashcardSets, studiedSets, navigation]);

  const handleAddFlashcard = () => {
    navigation.navigate('CreateFlashcard', { setId });
  };

  const handlePlaySet = () => {
    navigation.navigate('Play', { setId });
  };

  const handleEditSet = () => {
    navigation.navigate('EditSet', { setId });
  };

  const handleDeleteSet = () => {
    setDeleteSetModal(true);
  };

  const confirmDeleteSet = () => {
    if (setId) {
      deleteFlashcardSet(setId);
      navigation.goBack();
    }
    setDeleteSetModal(false);
  };

  const cancelDeleteSet = () => {
    setDeleteSetModal(false);
  };

  const handleFlashcardPress = (card) => {
    navigation.navigate('EditFlashcard', { cardId: card.id });
  };

  const handleDeleteFlashcard = (e, cardId) => {
    e.stopPropagation();
    setDeleteCardModal({ visible: true, cardId });
  };

  const confirmDeleteFlashcard = () => {
    if (deleteCardModal.cardId) {
      deleteFlashcard(deleteCardModal.cardId);
      setDeleteCardModal({ visible: false, cardId: null });
    }
  };

  const cancelDeleteFlashcard = () => {
    setDeleteCardModal({ visible: false, cardId: null });
  };

  if (!setItem) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.loadingContainer}>
          <Text style={[styles.loadingText, { color: theme.text }]}>
            {t('common.loading')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <View style={styles.setHeader}>
            <View style={[styles.setColorDot, { backgroundColor: setItem.color || '#4A90E2' }]} />
            <Text style={[styles.setName, { color: theme.text }]}>
              {setItem.name.toUpperCase()}
            </Text>
          </View>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={handleEditSet}
          >
            <Edit sx={{ fontSize: 20, color: theme.text }} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={handleDeleteSet}
          >
            <Delete sx={{ fontSize: 20, color: theme.error }} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Set Description */}
        {setItem.description && (
          <View style={[styles.descriptionCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.descriptionText, { color: theme.textSecondary }]}>
              {setItem.description}
            </Text>
          </View>
        )}

        {/* Set Stats */}
        <View style={[styles.statsCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: theme.primaryLight }]}>
                <Style sx={{ fontSize: 24, color: theme.primary }} />
              </View>
              <Text style={[styles.statValue, { color: theme.text }]}>
                {setFlashcards.length}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                {t('home.stats.cards')}
              </Text>
            </View>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: theme.primaryLight }]}>
                <PlayArrow sx={{ fontSize: 24, color: theme.primary }} />
              </View>
              <Text style={[styles.statValue, { color: theme.text }]}>
                {setItem.timesPlayed || 0}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                {t('set.plays')}
              </Text>
            </View>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.actionButton, styles.playButton, { backgroundColor: theme.primary }]}
            onPress={handlePlaySet}
          >
            <PlayArrow sx={{ fontSize: 20, color: 'white' }} />
            <Text style={[styles.actionButtonText, { color: 'white' }]}>
              {t('set.playSet')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, styles.addButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={handleAddFlashcard}
          >
            <Add sx={{ fontSize: 20, color: theme.primary }} />
            <Text style={[styles.actionButtonText, { color: theme.primary }]}>
              {t('set.addFlashcard')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Flashcards Section */}
        <Text style={[styles.sectionTitle, { color: theme.text }]}>
          {t('flashcard.flashcards')}
        </Text>

        {/* Empty State */}
        {setFlashcards.length === 0 && (
          <View style={styles.emptyState}>
            <LibraryBooks sx={{ fontSize: 64, color: theme.textMuted }} />
            <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
              {t('flashcard.noFlashcards')}
            </Text>
            <Text style={[styles.emptyStateSubtitle, { color: theme.textSecondary }]}>
              {t('flashcard.createFlashcardsSubtitle')}
            </Text>
          </View>
        )}

        {/* Flashcards List */}
        {setFlashcards.length > 0 && (
          <View style={styles.flashcardsList}>
            {setFlashcards.map((card) => (
              <TouchableOpacity
                key={card.id}
                style={[styles.flashcardCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => handleFlashcardPress(card)}
              >
                <View style={styles.flashcardContent}>
                  <View style={styles.flashcardRow}>
                    <View style={styles.flashcardSection}>
                      <View style={styles.flashcardLabelRow}>
                        <Text style={[styles.flashcardLabel, { color: theme.textMuted }]}>
                          {t('flashcard.question')}:
                        </Text>
                        <Text style={[styles.flashcardText, { color: theme.text }]}>
                          {card.front}
                        </Text>
                      </View>
                    </View>
                    <View style={[styles.flashcardDivider, { backgroundColor: theme.border }]} />
                    <View style={styles.flashcardSection}>
                      <View style={styles.flashcardLabelRow}>
                        <Text style={[styles.flashcardLabel, { color: theme.textMuted }]}>
                          {t('flashcard.answer')}:
                        </Text>
                        <Text style={[styles.flashcardText, { color: theme.text }]}>
                          {card.back}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.deleteFlashcardButton}
                  onPress={(e) => handleDeleteFlashcard(e, card.id)}
                >
                  <Delete sx={{ fontSize: 18, color: theme.error }} />
                </TouchableOpacity>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Delete Set Confirmation Modal */}
      {deleteSetModal && (
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              {t('set.deleteSet')}
            </Text>
            <Text style={[styles.modalMessage, { color: theme.textSecondary }]}>
              {t('set.deleteSetMessage')}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={cancelDeleteSet}
              >
                <Text style={[styles.modalButtonText, { color: theme.text }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.error, borderColor: theme.error }]}
                onPress={confirmDeleteSet}
              >
                <Text style={[styles.modalButtonText, { color: 'white' }]}>
                  {t('common.delete')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Delete Flashcard Confirmation Modal */}
      {deleteCardModal.visible && (
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              {t('flashcard.deleteFlashcard')}
            </Text>
            <Text style={[styles.modalMessage, { color: theme.textSecondary }]}>
              {t('flashcard.deleteFlashcardMessage')}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.border }]}
                onPress={cancelDeleteFlashcard}
              >
                <Text style={[styles.modalButtonText, { color: theme.text }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.error }]}
                onPress={confirmDeleteFlashcard}
              >
                <Text style={[styles.modalButtonText, { color: theme.buttonText }]}>
                  {t('common.delete')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Alert */}
      <CustomAlert
        visible={alert.visible}
        title={alert.title}
        message={alert.message}
        buttons={[
          {
            text: 'OK',
            onPress: () => {},
          },
        ]}
        onClose={() => setAlert({ ...alert, visible: false })}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerContent: {
    flex: 1,
  },
  setHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  setColorDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 12,
  },
  setName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  headerButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 40,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
  },
  descriptionCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: '1.5',
    display: 'block',
  },
  statsCard: {
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    marginBottom: 16,
  },
  statsGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
    justifyContent: 'center',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 14,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  actionButton: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  playButton: {
    borderWidth: 0,
  },
  addButton: {
    borderWidth: 1,
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
    display: 'block',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    marginBottom: 24,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    display: 'block',
  },
  emptyStateSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    display: 'block',
  },
  flashcardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  flashcardCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    position: 'relative',
  },
  flashcardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  flashcardRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  flashcardSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  flashcardLabelRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  flashcardDivider: {
    width: 1,
    minHeight: 20,
  },
  flashcardLabel: {
    fontSize: 14,
    fontWeight: '600',
    flexShrink: 0,
    lineHeight: 1.8,
  },
  flashcardText: {
    fontSize: 14,
    lineHeight: 1.8,
    flex: 1,
  },
  deleteFlashcardButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 4,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: '90%',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 12,
    display: 'block',
  },
  modalMessage: {
    fontSize: 14,
    marginBottom: 24,
    lineHeight: '1.5',
    display: 'block',
  },
  modalButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    borderWidth: 1,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default SetDetailScreen;
